import { useState, useEffect, type ReactNode } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { H2, Button, ButtonVariant } from '@meiuca/ui-components'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { VenueDetailHeader } from '@/components/sdui/venue-detail/VenueDetailHeader'
import { VenueDetailCTA } from '@/components/sdui/venue-detail/VenueDetailCTA'
import { VenueDetailContact } from '@/components/sdui/venue-detail/VenueDetailContact'
import { VenueDetailCourts } from '@/components/sdui/venue-detail/VenueDetailCourts'
import { VenueDetailOperatingHours } from '@/components/sdui/venue-detail/VenueDetailOperatingHours'
import { VenueDetailAmenities } from '@/components/sdui/venue-detail/VenueDetailAmenities'
import { VenueDetailDayUse } from '@/components/sdui/venue-detail/VenueDetailDayUse'
import { VenueDetailUpcomingOccurrences } from '@/components/sdui/venue-detail/VenueDetailUpcomingOccurrences'
import { arenaService } from '@/services/arenaService'
import { modalitiesManager } from '@/managers/modalitiesManager'
import type { VenueDetailResponse } from '@/types/sdui-arena-detail'
import { analyticsService } from '@meiuca/analytics'

// Deduplicate fetches in dev StrictMode (double mount) without pushing caching into the service
const venueDetailRequests = new Map<string, Promise<VenueDetailResponse>>()

async function fetchVenueDetailOnce(venueId: string) {
  const existing = venueDetailRequests.get(venueId)
  if (existing) return existing

  const request = arenaService.getVenueDetail(venueId).finally(() => {
    venueDetailRequests.delete(venueId)
  })
  venueDetailRequests.set(venueId, request)
  return request
}

export function ArenaDetailPage() {
  const { arenaId } = useParams<{ arenaId: string }>()
  const navigate = useNavigate()
  const [venue, setVenue] = useState<VenueDetailResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isActive = true

    async function fetchVenueDetail() {
      if (!arenaId) {
        setError('ID da arena não fornecido')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        // Load modalities in parallel with venue detail
        const [data] = await Promise.all([
          fetchVenueDetailOnce(arenaId),
          modalitiesManager.loadModalities()
        ])

        if (isActive) {
          setVenue(data)

          // Track screen view
          analyticsService.trackScreenView('arena_detail')
          analyticsService.track('arena_detail_viewed', {
            arena_id: arenaId,
            arena_name: data.header.name
          })
        }
      } catch (err) {
        console.error('Error fetching venue detail:', err)
        if (isActive) {
          setError('Não foi possível carregar os detalhes da arena')
        }
      } finally {
        if (isActive) {
          setIsLoading(false)
        }
      }
    }

    fetchVenueDetail()

    return () => {
      isActive = false
    }
  }, [arenaId])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-primary">
        <MarketplaceHeader />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-primary border-r-transparent mb-4" />
            <p className="text-text-secondary">Carregando detalhes da arena...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !venue) {
    return (
      <div className="min-h-screen bg-background-primary">
        <MarketplaceHeader />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <H2 className="mb-4">{error || 'Arena não encontrada'}</H2>
            <Button variant={ButtonVariant.PRIMARY} onClick={() => navigate('/')}>Voltar para o início</Button>
          </div>
        </div>
      </div>
    )
  }

  // Content sections composed by the client (order/presence owned here).
  // A section is shown only when its data is present/non-empty.
  const contentSections: Array<{ key: string; node: ReactNode }> = []

  if (venue.contact) {
    contentSections.push({
      key: 'contact',
      node: <VenueDetailContact config={{ id: 'contact', ...venue.contact }} />
    })
  }
  if (venue.courts.length > 0) {
    contentSections.push({
      key: 'courts',
      node: <VenueDetailCourts config={{ id: 'courts', title: 'Quadras', courts: venue.courts }} />
    })
  }
  if (venue.operatingHours.length > 0) {
    contentSections.push({
      key: 'operating-hours',
      node: <VenueDetailOperatingHours config={{ id: 'operating-hours', hours: venue.operatingHours }} />
    })
  }
  if (venue.amenities.length > 0) {
    contentSections.push({
      key: 'amenities',
      node: <VenueDetailAmenities config={{ id: 'amenities', title: 'Comodidades', amenities: venue.amenities }} />
    })
  }
  if (venue.dayUse.length > 0) {
    contentSections.push({
      key: 'day-use',
      node: <VenueDetailDayUse config={{ id: 'day-use', title: 'Horários', modalities: venue.dayUse }} />
    })
  }
  if (venue.upcomingOccurrences.length > 0) {
    contentSections.push({
      key: 'upcoming-occurrences',
      node: (
        <VenueDetailUpcomingOccurrences
          config={{ id: 'upcoming-occurrences', title: 'Próximos Jogos', modalities: venue.upcomingOccurrences }}
        />
      )
    })
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />

      {/* Content - constrained width with consistent section spacing */}
      {/* Hero/cover temporarily removed. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8 border-b border-border-light">
          <VenueDetailHeader config={{ id: 'header', ...venue.header }} />
        </div>

        <VenueDetailCTA
          config={{
            id: 'cta',
            title: 'Reserve sua quadra agora',
            subtitle: 'Escolha o melhor horário para você',
            ctaText: 'Reservar quadra',
            ctaAction: 'reserve',
            variant: 'both'
          }}
          venueId={venue.venueId}
        />

        {contentSections.map((section, index) => (
          <div
            key={section.key}
            className={`py-8 ${index < contentSections.length - 1 ? 'border-b border-border-light' : ''}`}
          >
            {section.node}
          </div>
        ))}
      </div>
    </div>
  )
}
